import {
  createContext,
  useContext,
  useLayoutEffect,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";

/**
 * #1721 #3564: the hub tab actually on screen. useHubTabs sets it on every
 * hub and "About this page" reads it before ?tab=, so a deep link to a tab the
 * role cannot open (the hub falls back to its default) never documents the
 * tab that was not shown.
 *
 * It is not a PageHeaderState field: each useSetPageHeader call replaces that
 * whole object, and on Cases the header is set by a tab, not by the hub. Its
 * own module, so the many tests that stub PageHeaderContext still render a
 * hub (outside a provider the setter is a no-op).
 */
const SetDocTabContext = createContext<Dispatch<SetStateAction<string | null>>>(() => {});
const DocTabValueContext = createContext<string | null>(null);

export function DocTabProvider({ children }: { children: ReactNode }) {
  const [docTab, setDocTab] = useState<string | null>(null);
  return (
    <SetDocTabContext.Provider value={setDocTab}>
      <DocTabValueContext.Provider value={docTab}>{children}</DocTabValueContext.Provider>
    </SetDocTabContext.Provider>
  );
}

/** The tab a hub is showing, or null on a page that is not a hub. */
export function useDocTab(): string | null {
  return useContext(DocTabValueContext);
}

/**
 * Called by useHubTabs with the resolved tab. Set in a layout effect, like the
 * page header, so the footer never paints the URL's tab first; cleared on
 * unmount so the next page starts from its own URL.
 */
export function useSetDocTab(tab: string): void {
  const setDocTab = useContext(SetDocTabContext);
  useLayoutEffect(() => {
    setDocTab(tab);
  }, [setDocTab, tab]);
  useLayoutEffect(() => () => setDocTab(null), [setDocTab]);
}
